// Os tipos do que o PDV chama de "conta": as linhas de orderInfo que o back-end
// devolve, as taxas, os produtos e os totais que a tela mostra.
//
// Antes tudo isso era `any` dentro de PointOfSale.components.tsx. Estes tipos
// não mudam nada em tempo de execução — descrevem o que já existe, para que
// dinheiro e pedido deixem de ser opacos.

/**
 * Uma linha de `orderInfo`. O back-end guarda tudo num par
 * (tipo, valor em texto): 1=item, 2=nº de pessoas, 3=produto, 7=taxas,
 * 8=desconto, 9=endereço, 10=pagamento, 14=impresso.
 * `orderInfoValue` chega como JSON em texto e é lido com JSON.parse.
 */
export interface LinhaDeInfoDaConta {
  orderInfoUuid?: string;
  orderInfoType: number;
  orderInfoValue: any;
  createdAt?: string;
  [outros: string]: any;
}

/**
 * Uma taxa da conta, como está gravada no orderInfo de tipo 7.
 * `taxValue` é TEXTO e pode ser percentual ("10%") ou valor fixo ("5,00").
 * `taxPeople` ausente significa "vale para todos os lugares da mesa".
 */
export interface TaxaDaConta {
  taxType?: string;
  taxValue?: string;
  taxPeople?: string;
  [outros: string]: any;
}

/** Uma observação de produto, já normalizada para a tela. */
export interface ObservacaoDeProduto {
  observationUuid: string;
  observationName: string;
  observationPrice: number;
  /** Só existe depois que a tela marca a observação. */
  observationSelected?: boolean;
  productObservationProduced?: boolean;
  [outros: string]: any;
}

/**
 * Um produto da conta como a tela o consome. Os valores de dinheiro são
 * TEXTO em reais com vírgula decimal ("12,50") — é assim que a tela desenha.
 */
export interface ProdutoDaConta {
  productOrderUuid: string;
  productSelected: boolean;
  productSemiSelected: boolean;
  /** Não existe quando a conta foi aberta pela tabela de pedidos. */
  productExpanded?: boolean;
  productUuid: string;
  productCode: string;
  productPrinted: boolean;
  productName: string;
  productPrice: string;
  seller: string | null | undefined;
  productDiscount: string;
  productTotalPrice: string;
  /** Só na mesa: o lugar a que o produto pertence. */
  people?: string;
  productOrderedAt: string;
  productObservations?: ObservacaoDeProduto[];
  productComboInfo?: any;
  /**
   * Declarado no estado do pedido desde sempre e NUNCA gravado por ninguém —
   * fica opcional para o tipo não mentir. Ver relatório do refactor.
   */
  productQuantity?: string;
  peopleNumber?: string;
}

/** Um pagamento já lançado na conta. */
export interface PagamentoDaConta {
  paymentMethodUuid: string;
  paymentValue: string;
}

/**
 * Os totais da conta. Todos em TEXTO com vírgula decimal, porque é o que a
 * tela imprime — menos `subtotal`, que é numérico e nunca é lido (ver
 * relatório do refactor).
 */
export interface TotaisDaConta {
  totalProductsPrice: string;
  totalDiscount: string;
  totalProductsPriceWithDiscount: string;
  subtotal?: number;
  serviceTax: string;
  entranceTax: string;
  minimumConsumption: string;
  parkTax: string;
  deliveryTax: string;
  customerBalance: string;
  total: string;
}

/**
 * Filtra as linhas de orderInfo de um tipo. Entrou no lugar do
 * `orderInfo.filter((info: any) => info.orderInfoType === N)` que se repetia
 * dezenas de vezes.
 */
export function infosDoTipo(
  orderInfo: LinhaDeInfoDaConta[],
  tipo: number
): LinhaDeInfoDaConta[] {
  return orderInfo.filter((info) => info.orderInfoType === tipo);
}
